import React, { useCallback, useMemo } from 'react'

import { useDispatch } from 'react-redux'

//UI
import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
} from '@chakra-ui/react'

//Hooks
// eslint-disable-next-line max-len

import { DummyInput } from '@/components/Form'

import { createExcelWithOptions } from '@/utils/fileHelper'
import { isNumber } from '@/utils/helper'
import { numberWithCommas } from '@/utils/stringHelper'
import moment from 'moment'

import { hideModal } from '@/stores/ui/modal/modalSlice'
import { COST_BUDGET_HEADER_MAPPING, CostBudgetType, getQuotationDetailTableColumnIds } from '@eapproval/offer-shared'
import { useTranslation } from 'react-i18next'
import QuotationDetailTable from './QuotationDetailTable'

const QuotationDetailModal = ({ history, costBudgetType, RFQCode, vendorIndex }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const closeModal = () => {
    //Reset

    dispatch(hideModal())
  }

  const discountRate = useMemo(() => {
    if (!isNumber(history?.budget) || !isNumber(history?.discountAmount)) return 0

    const originalPrice = history.budget + history.discountAmount

    return numberWithCommas((history.discountAmount / originalPrice) * 100)
  }, [history?.budget, history?.discountAmount])

  const handleDownloadQuotationDetail = useCallback(async () => {
    try {
      if (!history?.quotationDetails) return
      const quotationDetailColumns = getQuotationDetailTableColumnIds({
        costBudgetType,
        isTemplate: false,
        isExcel: true,
      })
      const header = quotationDetailColumns.map((col) => {
        //find header

        let header = '(-)'
        if (Object.keys(COST_BUDGET_HEADER_MAPPING).includes(col)) {
          header = COST_BUDGET_HEADER_MAPPING[col]
        }
        return {
          header: header,
          key: col,
          width: 10,
        }
      })

      const quotationDetails = history?.quotationDetails ?? []
      const formattedData = quotationDetails.map((row) => {
        const newRow = {}
        Object.keys(row).forEach((key) => {
          if (key.includes('Date') || key.includes('date') || key.includes('createdAt') || key.includes('updatedAt')) {
            if (row[key] instanceof Date) {
              newRow[key] = moment(row[key]).format('DD-MMM-YYYY')
            } else {
              newRow[key] = row[key]
            }
          } else {
            newRow[key] = row[key]
          }
        })

        return newRow
      })

      const excelBlob = await createExcelWithOptions(header, formattedData, [])

      saveAs(excelBlob, `${RFQCode}-ver${history.vendorHistoryIndex + 1}.xlsx`)
    } catch (err) {
      console.log(err)
    }
  }, [costBudgetType, RFQCode, vendorIndex])

  return (
    <Modal isOpen={true} onClose={closeModal} size="lg">
      <ModalOverlay />
      <form>
        <ModalContent maxW={'90%'}>
          <ModalHeader>
            {t('VENDOR_UPLOAD_PAGE_QUOTATION_DETAIL_MODAL')} - {RFQCode}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction={'column'} gap="3">
              <SimpleGrid columns={4} spacing={'20px'}>
                <DummyInput value={history?.currency} label={t('VENDOR_UPLOAD_PAGE_CURRENCY')} isRow />
                <DummyInput
                  value={`$${numberWithCommas(history?.budget)}`}
                  label={t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNTED_AMOUNT')}
                  isRow
                />
                <DummyInput
                  value={`$${numberWithCommas(history?.discountAmount)}`}
                  label={t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNT_AMOUNT')}
                  isRow
                />
                <DummyInput
                  value={`${discountRate}%`}
                  label={t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNT_RATE_LABEL')}
                  isRow
                />

                <DummyInput value={history?.remark} label={t('VENDOR_UPLOAD_PAGE_REMARKS')} isRow />
                <DummyInput
                  value={moment(history?.validDate).format('DD-MMM-YYYY')}
                  label={t('VENDOR_UPLOAD_PAGE_VALID_DATE')}
                  isRow
                />
                <DummyInput value={history?.creditTerm} label={t('VENDOR_UPLOAD_PAGE_CREDIT_TERMS')} isRow />
                <DummyInput value={history?.paymentTerm} label={t('VENDOR_UPLOAD_PAGE_PAYMENT_TERMS')} isRow />
              </SimpleGrid>

              <SimpleGrid columns={4} spacing={'20px'}>
                {costBudgetType === CostBudgetType.SUPPLY_MATERIAL_ONLY && (
                  <>
                    <DummyInput value={history?.warranty} label={t('VENDOR_UPLOAD_PAGE_WARRANTY')} isRow />
                    <DummyInput value={history?.incoterm} label={t('VENDOR_UPLOAD_PAGE_INCOTERM')} isRow />
                    <DummyInput value={history?.leadTime} label={t('VENDOR_UPLOAD_PAGE_LEAD_TIME')} isRow />
                  </>
                )}
                {[CostBudgetType.SUPPLY_SITE_WORK, CostBudgetType.SUPPLY_SITE_WORK_RFQ].includes(costBudgetType) && (
                  <>
                    <DummyInput value={history?.insurance} label={t('VENDOR_UPLOAD_PAGE_INSURANCE')} isRow />
                    <DummyInput value={history?.incoterm} label={t('VENDOR_UPLOAD_PAGE_INCOTERM')} isRow />
                    <DummyInput value={history?.leadTime} label={t('VENDOR_UPLOAD_PAGE_LEAD_TIME')} isRow />
                    <DummyInput value={history?.DLP} label={t('VENDOR_UPLOAD_PAGE_DLP')} isRow />
                  </>
                )}

                {(costBudgetType === CostBudgetType.SUPPLY_ENGINEERING_SERVICE ||
                  costBudgetType === CostBudgetType.SUPPLY_MAINTENANCE_SERVICE) && (
                  <DummyInput value={history?.insurance} label={t('VENDOR_UPLOAD_PAGE_INSURANCE')} isRow />
                )}
              </SimpleGrid>

              <Flex justifyItems={'flex-end'} w="100%" alignItems="center" gap="3">
                <Button colorScheme={'green'} onClick={handleDownloadQuotationDetail}>
                  {t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DOWNLOAD')}
                </Button>
              </Flex>

              <QuotationDetailTable data={history?.quotationDetails ?? []} costBudgetType={costBudgetType} disabled />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button colorScheme="gray" onClick={closeModal}>
                {t('FILE_HISTORY_MODAL_CLOSE')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  )
}

export default QuotationDetailModal
